import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox, InputAdornment } from '@mui/material';
import Switch from '@mui/material/Switch';
import { Dialog, Autocomplete, TextField, IconButton, Button } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { onCheckBoxChange, addPropertyPrefix } from 'utils/propertyChangeAdapters';
import { emptyString, emptyArray } from 'constants/emptyConst';
import Search from '@mui/icons-material/Search';
import { Prompt } from 'react-router-dom';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import PFContainerWrapper from 'components/common/select/BrowserContainer/individualPartners/PFContainerWrapper';
import WrappedFakeText from 'components/common/WrappedFakeText';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';

export const OfferAssetPFClientComponent = ({
  onPropertyChange,
  onUniqueIdBlur,
  t,
  client,
  validationInfo,
  relations,
  rights,
  gdpr,
  onGDPRPrint,
  defaultRelationId,
  onSave,
  saving,
  setIsPartnerFromModal,
  printingGdpr,
  loadGDPR,
  isDirty,
  processing,
  isClientLoaded,
  resetGDPR,
  dirtyInfo
}) => {
  const { dealerContactSegregation } = useContext(ApplicationFeaturesContext);
  const { masterPartnerSearch } = useContext(GlobalRightsContext);
  const isValid = validationInfo.isValid;
  const generalData = client.generalData || {};
  const generalDataValidationInfo = validationInfo.generalData || {};
  const contactData = client.contactData || {};
  const contactDataValidationInfo = validationInfo.contactData || {};
  const [openGDPR, setOpenGDPR] = useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const dealerContactEmails = client.dealerContactEmails?.filter(x => x.isActive) || emptyArray;
  const dealerContactPhones = client.dealerContactPhones?.filter(x => x.isActive) || emptyArray;

  const handleClientAgreesWithPersonalDataUse = value => {
    if (value) {
      loadGDPR();
      setOpenGDPR(value);
    } else {
      resetGDPR();
      onPropertyChange('agreesWithPersonalDataUse', false);
    }
  };

  const handleGDPRPositive = () => {
    onPropertyChange('agreesWithPersonalDataUse', true);
    setOpenGDPR(false);
  };

  const handleGDPRClose = () => {
    setOpenGDPR(false);
  };

  const CustomEndIcons = ({ onCustomClick, customDisabled }) => (
    <InputAdornment position='end'>
      <IconButton disabled={customDisabled} color='primary' variant='text' size='tiny' onClick={onCustomClick}>
        <Search />
      </IconButton>
    </InputAdornment>
  );

  const loadDealerContactEmails = useCallback(
    input => {
      if (!input) {
        return Promise.resolve(dealerContactEmails);
      }
      return Promise.resolve(dealerContactEmails?.filter(x => x.email.toLowerCase().includes(input.toLowerCase())));
    },
    [dealerContactEmails]
  );

  const loadDealerContactPhones = useCallback(
    input => {
      if (!input) {
        return Promise.resolve(dealerContactPhones);
      }
      return Promise.resolve(dealerContactPhones?.filter(x => x.phone.toLowerCase().includes(input.toLowerCase())));
    },
    [dealerContactPhones]
  );

  const onAutocompleteChange = onPropertyChange => suggestion => {
    if (suggestion) {
      onPropertyChange(suggestion.dealerContactModeId ? suggestion.dealerContactModeId : suggestion.email || suggestion.phone);
    }
  };

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid item xs={12} md={8}>
        <FormControlLabel
          control={
            <Checkbox
              id={'offerAssetClientCard_pfPartner_agreesWithPersonalDataUse'}
              checked={Boolean(client.agreesWithPersonalDataUse)}
              onChange={onCheckBoxChange(handleClientAgreesWithPersonalDataUse)}
              color='primary'
              name='agreesWithPersonalDataUse'
              disabled={
                (client.masterPartnerId && !dirtyInfo.agreesWithPersonalDataUse) ||
                rights.readOnlyFull ||
                !rights.financedAssetClientWrite ||
                processing
              }
            />
          }
          label={t('OfferEdit.Client.AgreesWithPersonalDataUse')}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              id={'offerAssetClientCard_pfPartner_isExternal'}
              checked={generalData.isExternal || false}
              onChange={onCheckBoxChange(addPropertyPrefix('generalData', onPropertyChange)('isExternal'))}
              name='isExternal'
              disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
            />
          }
          label={t('OfferEdit.Client.External')}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <PFContainerWrapper
          disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          label={t('OfferEdit.Client.PersonalIdentifier')}
          onTextFieldChange={addPropertyPrefix('generalData', onPropertyChange)('uniqueId')}
          onBlur={() => onUniqueIdBlur(generalData.uniqueId)}
          value={generalData.uniqueId}
          iconDisabled={false}
          iconHidden={!masterPartnerSearch}
          error={!!validationInfo.generalData?.uniqueId}
          helperText={validationInfo.generalData?.uniqueId}
          setIsBrowserOpen={setIsBrowserOpen}
          isBrowserOpen={isBrowserOpen}
          hasCustomIcons
          customEndIcons={
            <CustomEndIcons
              onCustomClick={() => {
                setIsPartnerFromModal(true);
                setIsBrowserOpen(true);
              }}
              customDisabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Autocomplete
          id={'offerAssetClientCard_pfPartner_relation'}
          disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          fullWidth
          isClearable={false}
          value={generalData.relationId || defaultRelationId || -1}
          error={Boolean(generalDataValidationInfo.relationId)}
          helperText={generalDataValidationInfo.relationId}
          onChange={addPropertyPrefix('generalData', onPropertyChange)('relationId')}
          label={t('OfferEdit.Client.Relation')}
          simpleValue={true}
          valueKey='id'
          name='relation'
          options={relations}
          placeholder={t('OfferEdit.Client.NoRelation')}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pfPartner_firstName'}
          disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          fullWidth
          value={generalData.firstName || ''}
          name='firstName'
          label={t('OfferEdit.Client.FirstName')}
          error={Boolean(validationInfo.generalData?.firstName)}
          helperText={validationInfo.generalData?.firstName}
          onChange={addPropertyPrefix('generalData', onPropertyChange)('firstName')}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pfPartner_lastName'}
          disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
          fullWidth
          value={generalData.lastName || ''}
          name='lastName'
          label={t('OfferEdit.Client.LastName')}
          error={Boolean(validationInfo.generalData?.lastName)}
          helperText={validationInfo.generalData?.lastName}
          onChange={addPropertyPrefix('generalData', onPropertyChange)('lastName')}
        />
      </Grid>
      {dealerContactSegregation ? (
        <>
          <Grid item xs={12} sm={12} md={6}>
            <Autocomplete
              id={'offerAssetClientCard_pfPartner_mobilePhone'}
              disabled={
                !client.agreesWithPersonalDataUse ||
                rights.readOnlyFull ||
                !rights.financedAssetClientWrite ||
                processing ||
                !generalData.uniqueId
              }
              fullWidth
              value={client.contactData || emptyString}
              valueKey='dealerContactModeId'
              labelKey='phone'
              label={t('OfferEdit.Client.MobilePhone')}
              loadOptions={loadDealerContactPhones}
              options={dealerContactPhones}
              isClearable={false}
              onChange={onAutocompleteChange(addPropertyPrefix('contactData', onPropertyChange)('dealerContactModePhoneId'))}
              creatable={true}
              error={Boolean(contactDataValidationInfo.phone)}
              helperText={contactDataValidationInfo.phone}
              cache={false}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Autocomplete
              id={'offerAssetClientCard_pfPartner_email'}
              disabled={
                !client.agreesWithPersonalDataUse ||
                rights.readOnlyFull ||
                !rights.financedAssetClientWrite ||
                processing ||
                !generalData.uniqueId
              }
              fullWidth
              value={client.contactData || emptyString}
              valueKey='dealerContactModeId'
              labelKey='email'
              label={t('OfferEdit.Client.Email')}
              loadOptions={loadDealerContactEmails}
              options={dealerContactEmails}
              isClearable={false}
              onChange={onAutocompleteChange(addPropertyPrefix('contactData', onPropertyChange)('dealerContactModeEmailId'))}
              creatable={true}
              error={Boolean(contactDataValidationInfo.email)}
              helperText={contactDataValidationInfo.email}
              cache={false}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={12} md={6}>
            <PhoneNumberField
              id={'offerAssetClientCard_pfPartner_mobilePhone'}
              country={contactData.phoneCountryCode}
              disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
              fullWidth
              value={contactData.phone || ''}
              error={Boolean(contactDataValidationInfo.phone)}
              name='phone'
              label={t('OfferEdit.Client.MobilePhone')}
              onChange={addPropertyPrefix('contactData', onPropertyChange)('phone')}
              helperText={contactDataValidationInfo.phone}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              id={'offerAssetClientCard_pfPartner_email'}
              disabled={!client.agreesWithPersonalDataUse || rights.readOnlyFull || !rights.financedAssetClientWrite || processing}
              fullWidth
              value={contactData.email || ''}
              error={Boolean(contactDataValidationInfo.email)}
              helperText={contactDataValidationInfo.email}
              name='email'
              label={t('OfferEdit.Client.Email')}
              onChange={addPropertyPrefix('contactData', onPropertyChange)('email')}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          id={'offerAssetClientCard_pfPartner_comment'}
          label={t('OfferEdit.Client.Comment')}
          multiline
          inputProps={{ maxLength: 255 }}
          rows={3}
          disabled={processing}
          value={contactData.comment || emptyString}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('comment')}
          variant='outlined'
          sx={{ mt: 1 }}
        />
      </Grid>

      {!rights.isImage && (
        <Grid item container xs={12} spacing={1} mt={1}>
          <Grid item>
            <Button
              id={'offerAssetClientCard_pfPartner_saveBtn'}
              onClick={onSave}
              disabled={
                saving || !isValid || rights.readOnlyFull || !rights.financedAssetClientWrite || !isDirty || processing || !isClientLoaded
              }
            >
              {saving ? t('Saving') : t('Save')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              id={'offerAssetClientCard_pfPartner_printGDPRBtn'}
              onClick={onGDPRPrint}
              disabled={
                printingGdpr ||
                rights.readOnlyFull ||
                !rights.financedAssetClientWrite ||
                (client.partnerType.typeCheck.isPF && !client.agreesWithPersonalDataUse) ||
                !client.masterPartnerId ||
                client.masterPartnerId == null ||
                processing
              }
            >
              {printingGdpr ? t('PrintingGDPR') : t('PrintGDPR')}
            </Button>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={openGDPR}
        onClose={handleGDPRClose}
        aria-labelledby='gdpr-title'
        aria-describedby='gdpr-description'
        title={t('OfferEdit.Client.GDPR.Title')}
        maxWidth='xs'
        content={!gdpr.isLoaded ? <WrappedFakeText /> : <div dangerouslySetInnerHTML={{ __html: gdpr.text }} />}
        actions={
          <>
            <Button
              id={'offerAssetClientCard_pfPartner_printGDPR_acceptBtn'}
              disabled={!gdpr.isLoaded || processing}
              color='primary'
              autoFocus
              onClick={handleGDPRPositive}
            >
              {t('OfferEdit.Client.GDPR.AcceptButton')}
            </Button>
            <Button
              id={'offerAssetClientCard_pfPartner_printGDPR_rejectBtn'}
              disabled={!gdpr.isLoaded || processing}
              color='primary'
              onClick={handleGDPRClose}
            >
              {t('OfferEdit.Client.GDPR.RejectButton')}
            </Button>
          </>
        }
      />
    </>
  );
};

OfferAssetPFClientComponent.propTypes = {
  t: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  relations: PropTypes.array.isRequired,
  rights: PropTypes.object.isRequired,
  gdpr: PropTypes.object.isRequired,
  loadGDPR: PropTypes.func.isRequired,
  onGDPRPrint: PropTypes.func.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  defaultRelationId: PropTypes.number,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  processing: PropTypes.bool.isRequired,
  isClientLoaded: PropTypes.bool,
  resetGDPR: PropTypes.func.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  onCustomClick: PropTypes.func,
  customDisabled: PropTypes.bool
};

export default compose(withTranslation())(withRights(OfferAssetPFClientComponent));
